/**
 * ConsentManager - Core consent logic with Google Consent Mode v2
 */

export class ConsentManager {
  constructor(config, translations) {
    this.config = config;
    this.t = translations || {};
    this.consent = {
      necessary: true, // Always true
      analytics: false,
      marketing: false,
    };

    // Initialize Google Consent Mode with denied defaults
    this.initGoogleConsentMode();

    // Set up script/iframe blocking
    this.setupBlocking();
  }

  /**
   * Initialize Google Consent Mode v2 with denied defaults
   */
  initGoogleConsentMode() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function() { window.dataLayer.push(arguments); };

    // Set default consent state (denied for EU)
    window.gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied',
      'functionality_storage': 'denied',
      'personalization_storage': 'denied',
      'security_storage': 'granted', // Always granted
      'wait_for_update': 500,
    });

    // Enable URL passthrough for better attribution
    window.gtag('set', 'url_passthrough', true);
    window.gtag('set', 'ads_data_redaction', true);

    if (this.config.debug) {
      console.log('[ConsentBanner] Google Consent Mode initialized with denied defaults');
    }
  }

  /**
   * Update Google Consent Mode based on user choice
   */
  updateGoogleConsentMode() {
    const consentUpdate = {
      'analytics_storage': this.consent.analytics ? 'granted' : 'denied',
      'ad_storage': this.consent.marketing ? 'granted' : 'denied',
      'ad_user_data': this.consent.marketing ? 'granted' : 'denied',
      'ad_personalization': this.consent.marketing ? 'granted' : 'denied',
      'functionality_storage': this.consent.analytics ? 'granted' : 'denied',
      'personalization_storage': this.consent.marketing ? 'granted' : 'denied',
    };

    window.gtag('consent', 'update', consentUpdate);

    if (this.config.debug) {
      console.log('[ConsentBanner] Google Consent Mode updated:', consentUpdate);
    }

    // Dispatch event for other scripts to listen
    window.dispatchEvent(new CustomEvent('consentUpdate', {
      detail: { consent: this.consent, googleConsent: consentUpdate }
    }));
  }

  /**
   * Set up blocking for scripts and iframes with data-consent attribute
   */
  setupBlocking() {
    // Block scripts with data-consent attribute
    this.blockScripts();

    // Block iframes (YouTube, Maps, etc.)
    this.blockIframes();

    // Observe DOM for dynamically added elements
    this.observeDOM();
  }

  /**
   * Block scripts that require consent
   */
  blockScripts() {
    const scripts = document.querySelectorAll('script[data-consent]');

    scripts.forEach(script => {
      const category = script.dataset.consent;

      if (!this.hasConsent(category)) {
        // Store original src and type
        if (script.src) {
          script.dataset.consentSrc = script.src;
          script.removeAttribute('src');
        }
        if (script.type !== 'text/plain') {
          script.dataset.consentType = script.type || 'text/javascript';
          script.type = 'text/plain';
        }
      }
    });
  }

  /**
   * Unblock scripts after consent
   */
  unblockScripts(category) {
    const scripts = document.querySelectorAll(`script[data-consent="${category}"]`);

    scripts.forEach(script => {
      if (script.dataset.consentSrc) {
        // Create new script element (required for execution)
        const newScript = document.createElement('script');
        newScript.src = script.dataset.consentSrc;
        newScript.type = script.dataset.consentType || 'text/javascript';

        // Copy other attributes
        Array.from(script.attributes).forEach(attr => {
          if (!['src', 'type', 'data-consent', 'data-consent-src', 'data-consent-type'].includes(attr.name)) {
            newScript.setAttribute(attr.name, attr.value);
          }
        });

        script.parentNode.replaceChild(newScript, script);

        if (this.config.debug) {
          console.log(`[ConsentBanner] Unblocked script: ${newScript.src}`);
        }
      } else if (script.type === 'text/plain' && script.textContent) {
        // Inline script
        const newScript = document.createElement('script');
        newScript.textContent = script.textContent;
        newScript.type = script.dataset.consentType || 'text/javascript';
        script.parentNode.replaceChild(newScript, script);

        if (this.config.debug) {
          console.log('[ConsentBanner] Unblocked inline script');
        }
      }
    });
  }

  /**
   * Auto-detect iframe service and return category
   */
  detectIframeCategory(src) {
    if (!src) return null;

    // Marketing category (tracking, social)
    const marketingDomains = [
      'youtube.com', 'youtu.be', 'youtube-nocookie.com',
      'facebook.com', 'fb.com', 'fbcdn.net',
      'instagram.com',
      'tiktok.com',
      'twitter.com', 'x.com',
      'linkedin.com',
      'pinterest.com',
      'vimeo.com',
      'dailymotion.com',
      'twitch.tv',
      'spotify.com',
      'soundcloud.com',
    ];

    // Analytics category
    const analyticsDomains = [
      'google.com/maps', 'maps.google',
      'openstreetmap.org',
      'bing.com/maps',
    ];

    const srcLower = src.toLowerCase();

    for (const domain of marketingDomains) {
      if (srcLower.includes(domain)) return 'marketing';
    }

    for (const domain of analyticsDomains) {
      if (srcLower.includes(domain)) return 'analytics';
    }

    return null;
  }

  /**
   * Block iframes (YouTube, Maps, etc.) with placeholder
   * Auto-detects iframes without data-consent attribute
   */
  blockIframes() {
    // First, handle iframes with explicit data-consent
    const explicitIframes = document.querySelectorAll('iframe[data-consent]');
    explicitIframes.forEach(iframe => {
      const category = iframe.dataset.consent;
      if (!this.hasConsent(category)) {
        this.createIframePlaceholder(iframe);
      }
    });

    // Then, auto-detect iframes without data-consent (YouTube, etc.)
    const allIframes = document.querySelectorAll('iframe:not([data-consent]):not([data-consent-processed])');
    allIframes.forEach(iframe => {
      const src = iframe.src || iframe.dataset.src;
      const category = this.detectIframeCategory(src);

      if (category && !this.hasConsent(category)) {
        // Mark as processed and set category
        iframe.dataset.consent = category;
        iframe.dataset.consentAuto = 'true';
        this.createIframePlaceholder(iframe);
      }
    });
  }

  /**
   * Create placeholder for blocked iframe
   */
  createIframePlaceholder(iframe) {
    // Mark as processed to avoid duplicate placeholders
    if (iframe.dataset.consentProcessed) return;
    iframe.dataset.consentProcessed = 'true';

    const category = iframe.dataset.consent;
    const src = iframe.src || iframe.dataset.src;

    // Store original src
    iframe.dataset.consentSrc = src;
    iframe.removeAttribute('src');

    // Create placeholder
    const placeholder = document.createElement('div');
    placeholder.className = 'consent-iframe-placeholder';
    placeholder.dataset.consentCategory = category;

    // Detect service type
    let serviceName = 'external content';
    let icon = '🔒';

    if (src.includes('youtube') || src.includes('youtu.be')) {
      serviceName = 'YouTube';
      icon = '▶️';
    } else if (src.includes('google.com/maps') || src.includes('maps.google')) {
      serviceName = 'Google Maps';
      icon = '🗺️';
    } else if (src.includes('facebook.com') || src.includes('fb.com')) {
      serviceName = 'Facebook';
      icon = '📘';
    } else if (src.includes('instagram.com')) {
      serviceName = 'Instagram';
      icon = '📷';
    } else if (src.includes('vimeo.com')) {
      serviceName = 'Vimeo';
      icon = '🎬';
    } else if (src.includes('spotify.com')) {
      serviceName = 'Spotify';
      icon = '🎵';
    }

    // Get translation for placeholder
    const blockedText = this.t.contentBlocked?.replace('{service}', serviceName) || `${serviceName} content blocked`;
    const allowText = this.t.acceptAll || 'Accept all';
    const settingsText = this.t.cookieSettings || this.t.settings || 'Cookie settings';

    placeholder.innerHTML = `
      <div class="consent-iframe-placeholder__content">
        <span class="consent-iframe-placeholder__icon">${icon}</span>
        <p class="consent-iframe-placeholder__text">${blockedText}</p>
        <button class="consent-iframe-placeholder__button" data-consent-allow="${category}">
          ${allowText}
        </button>
        <a href="#" class="consent-iframe-placeholder__link" data-consent-settings>
          ${settingsText}
        </a>
      </div>
    `;

    // Copy dimensions
    placeholder.style.width = iframe.width ? `${iframe.width}px` : iframe.style.width || '100%';
    placeholder.style.height = iframe.height ? `${iframe.height}px` : iframe.style.height || '315px';

    // Replace iframe with placeholder
    iframe.style.display = 'none';
    iframe.parentNode.insertBefore(placeholder, iframe);

    // Add click handler for "Allow" button - accepts ALL cookies
    placeholder.querySelector('[data-consent-allow]').addEventListener('click', (e) => {
      e.preventDefault();
      this.acceptAll();
    });

    // Add click handler for "Cookie settings" link
    placeholder.querySelector('[data-consent-settings]').addEventListener('click', (e) => {
      e.preventDefault();
      if (window.ConsentBanner && window.ConsentBanner.showSettings) {
        window.ConsentBanner.showSettings();
      }
    });
  }

  /**
   * Allow single iframe without changing global consent
   */
  allowSingleIframe(iframe, placeholder) {
    iframe.src = iframe.dataset.consentSrc;
    iframe.style.display = '';
    placeholder.remove();

    if (this.config.debug) {
      console.log('[ConsentBanner] Single iframe allowed:', iframe.dataset.consentSrc);
    }
  }

  /**
   * Unblock all iframes for category
   */
  unblockIframes(category) {
    const placeholders = document.querySelectorAll(`.consent-iframe-placeholder[data-consent-category="${category}"]`);

    placeholders.forEach(placeholder => {
      const iframe = placeholder.nextElementSibling;
      if (iframe && iframe.tagName === 'IFRAME') {
        iframe.src = iframe.dataset.consentSrc;
        iframe.style.display = '';
        placeholder.remove();

        if (this.config.debug) {
          console.log('[ConsentBanner] Unblocked iframe:', iframe.dataset.consentSrc);
        }
      }
    });
  }

  /**
   * Observe DOM for dynamically added scripts/iframes
   */
  observeDOM() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check scripts
            if (node.tagName === 'SCRIPT' && node.dataset.consent) {
              if (!this.hasConsent(node.dataset.consent)) {
                this.blockScripts();
              }
            }
            // Check iframes (with data-consent or auto-detect)
            if (node.tagName === 'IFRAME') {
              this.processIframe(node);
            }
            // Check children
            const scripts = node.querySelectorAll?.('script[data-consent]');
            const iframes = node.querySelectorAll?.('iframe');
            scripts?.forEach(s => {
              if (!this.hasConsent(s.dataset.consent)) this.blockScripts();
            });
            iframes?.forEach(i => this.processIframe(i));
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Process single iframe (block if needed)
   */
  processIframe(iframe) {
    // Skip already processed
    if (iframe.dataset.consentProcessed) return;

    // If has explicit data-consent
    if (iframe.dataset.consent) {
      if (!this.hasConsent(iframe.dataset.consent)) {
        this.createIframePlaceholder(iframe);
      }
      return;
    }

    // Auto-detect category
    const src = iframe.src || iframe.dataset.src;
    const category = this.detectIframeCategory(src);

    if (category && !this.hasConsent(category)) {
      iframe.dataset.consent = category;
      iframe.dataset.consentAuto = 'true';
      this.createIframePlaceholder(iframe);
    }
  }

  /**
   * Check if consent given for category
   */
  hasConsent(category) {
    if (category === 'necessary') return true;
    return this.consent[category] === true;
  }

  /**
   * Get current consent state
   */
  getConsent() {
    return { ...this.consent };
  }

  /**
   * Set consent for category
   */
  setConsent(category, value) {
    if (category === 'necessary') return; // Can't change necessary
    this.consent[category] = value;
  }

  /**
   * Accept all cookies
   */
  acceptAll(source = 'accept') {
    this.consent.analytics = true;
    this.consent.marketing = true;
    this.saveConsent();
    this.applyConsent();
    this.emitChoice(source);
  }

  /**
   * Reject all optional cookies
   */
  rejectAll(source = 'reject') {
    this.consent.analytics = false;
    this.consent.marketing = false;
    this.saveConsent();
    this.applyConsent();
    this.emitChoice(source);
  }

  /**
   * Save custom consent choices
   */
  saveCustom(choices, source = 'custom') {
    this.consent.analytics = choices.analytics || false;
    this.consent.marketing = choices.marketing || false;
    this.saveConsent();
    this.applyConsent();
    this.emitChoice(source);
  }

  /**
   * Emit a measurable "user made a choice" signal: window event `consentChoice`
   * + dataLayer event `consent_choice` (for GA4/GTM). `source` says how the
   * choice was made: accept | reject | custom | close | backdrop | implied | api.
   * Measurement only — no behaviour change.
   */
  emitChoice(source) {
    const detail = {
      source,
      ui: this.uiVariant(),
      analytics: !!this.consent.analytics,
      marketing: !!this.consent.marketing,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'consent_choice',
      consent_source: source,
      consent_ui: detail.ui,
      consent_analytics: detail.analytics ? 'granted' : 'denied',
      consent_marketing: detail.marketing ? 'granted' : 'denied',
    });
    window.dispatchEvent(new CustomEvent('consentChoice', { detail }));
  }

  /**
   * User closed the banner without choosing (X / backdrop). Nothing is stored in
   * a cookie (closing is neither consent nor rejection, ÚOOÚ FAQ), only a
   * sessionStorage flag so the banner does not pop up on every page of this
   * visit; next visit asks again. Emits a `consentChoice` with the given source
   * for measurement only.
   */
  postpone(source = 'close') {
    try {
      window.sessionStorage.setItem(this.config.cookieName + '_postponed', '1');
    } catch (e) {
      // storage blocked: banner will simply show again on next page
    }
    this.emitChoice(source);
  }

  isPostponed() {
    try {
      return window.sessionStorage.getItem(this.config.cookieName + '_postponed') === '1';
    } catch (e) {
      return false;
    }
  }

  /**
   * Verze UI pro měření: 'modal-lock' = centrovaný modal se zámkem scrollu a focus trapem
   * (od 2026-10-16), 'bar' = spodní/horní lišta. Starý modal bez zámku ui neposílal.
   */
  uiVariant() {
    return this.config.position === 'center' ? 'modal-lock' : 'bar';
  }

  /**
   * Emit "banner was shown" signal (window event `consentBannerShow` + dataLayer
   * `consent_banner_show`) so ignore-rate = shows - choices can be measured.
   */
  emitBannerShow() {
    window.dataLayer = window.dataLayer || [];
    const ui = this.uiVariant();
    window.dataLayer.push({ event: 'consent_banner_show', consent_ui: ui });
    window.dispatchEvent(new CustomEvent('consentBannerShow', { detail: { ui } }));
  }

  /**
   * Apply consent (unblock scripts, update Google Consent Mode)
   */
  applyConsent() {
    // Update Google Consent Mode
    this.updateGoogleConsentMode();

    // Unblock scripts and iframes
    if (this.consent.analytics) {
      this.unblockScripts('analytics');
      this.unblockIframes('analytics');
    }
    if (this.consent.marketing) {
      this.unblockScripts('marketing');
      this.unblockIframes('marketing');
    }

    if (this.config.debug) {
      console.log('[ConsentBanner] Consent applied:', this.consent);
    }
  }

  /**
   * Save consent to cookie
   */
  saveConsent() {
    // Accepted consent is valid cookieExpiry days (default 365, ÚOOÚ: ~12 months).
    // A rejection is remembered rejectExpiry days (default 180, ÚOOÚ/CNIL: do not
    // re-ask for at least 6 months), then the banner may ask again.
    const anyGranted = !!(this.consent.analytics || this.consent.marketing);
    const expiryDays = anyGranted ? this.config.cookieExpiry : this.config.rejectExpiry;

    const data = {
      consent: this.consent,
      timestamp: Date.now(),
      expiryDays,
      version: '1.0',
    };

    const expires = new Date();
    expires.setDate(expires.getDate() + expiryDays);

    document.cookie = `${this.config.cookieName}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    if (this.config.debug) {
      console.log('[ConsentBanner] Consent saved to cookie');
    }
  }

  /**
   * Check if consent already stored
   */
  hasStoredConsent() {
    const cookie = this.getCookie(this.config.cookieName);
    if (!cookie) return false;

    try {
      const data = JSON.parse(decodeURIComponent(cookie));

      // Check if consent is expired (older than expiryDays stored with the choice,
      // fallback cookieExpiry for cookies written before expiryDays existed)
      const days = Number.isFinite(data.expiryDays) ? data.expiryDays : this.config.cookieExpiry;
      const expiryMs = days * 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > expiryMs) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Apply stored consent from cookie
   */
  applyStoredConsent() {
    const cookie = this.getCookie(this.config.cookieName);
    if (!cookie) return;

    try {
      const data = JSON.parse(decodeURIComponent(cookie));
      this.consent = { ...this.consent, ...data.consent };
      this.applyConsent();

      if (this.config.debug) {
        console.log('[ConsentBanner] Stored consent applied:', this.consent);
      }
    } catch (e) {
      console.error('[ConsentBanner] Failed to parse stored consent:', e);
    }
  }

  /**
   * Reset consent (delete cookie)
   */
  reset() {
    document.cookie = `${this.config.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    this.consent = { necessary: true, analytics: false, marketing: false };
    this.initGoogleConsentMode();

    if (this.config.debug) {
      console.log('[ConsentBanner] Consent reset');
    }
  }

  /**
   * Get cookie value by name
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }
}
